import { isAxiosError } from 'axios';
import {
  AUTH_CHANNEL,
  http,
  jwt,
  useBroadcastChannel,
} from '@silvertours/front-shared';
import { useCallback } from 'react';
import {
  useAccountConfig,
  useWhitelabel,
  useConsent,
  Services,
} from '../Runtime';
import { useAccount } from './context';
import { generic as genericTracking } from '../analytics';

type SessionData = {
  securityTokenValue: string;
  isMobileUserAgent: boolean;
  gtmEnvironment: 'dev' | 'live';
  anonymizeIP: boolean;
};

enum LoginError {
  Blocked = 'blocked',
  ExpiredPassword = 'expiredPassword',
  Unauthorized = 'unauthorized',
  Unknown = 'unknown',
  UnverifiedEmail = 'unverifiedEmail',
  Validation = 'validation',
  WeakPassword = 'weakPassword',
  /*
   * The banner says only that the login did not work. The cause goes on the
   * `social_login_failed` event.
   *
   * `invalid-credentials` does not become `Validation`. That text names the
   * email and the password, and this path has no password.
   */
  SocialLoginFailed = 'socialLoginFailed',
  SocialLoginPopupBlocked = 'socialLoginPopupBlocked',
  SocialLoginUnavailable = 'socialLoginUnavailable',
}

type LoginResponse = {
  access_token: string;
  id_token: string;
  refresh_token: string;
};

type LoginErrorResponse = {
  type: string;
  details?: string[];
};

type LoginData = {
  loggedIn: boolean;
  error?: LoginError;
};

const handleLoginError = (error: string) => {
  switch (error) {
    case '/general/errors/bad-request':
    case '/auth/errors/login/bad-request':
    case '/auth/errors/login/invalid-credentials':
      return LoginError.Validation;
    case '/auth/errors/login/expired-password':
      return LoginError.ExpiredPassword;
    case '/auth/errors/login/unverified-email':
      return LoginError.UnverifiedEmail;
    case '/auth/errors/login/weak-password':
      return LoginError.WeakPassword;
    case '/general/errors/undefined':
    case '/auth/errors/login/undefined':
    default:
      return LoginError.Unknown;
  }
};

/**
 * Creates the server-side session and answers whether it exists.
 *
 * Any 2xx counts, the same way `login` reads its own response. The endpoint
 * answers 204, but this result now decides whether a login succeeds, and one
 * exact status would turn a 200 into a failed login for every user.
 */
const updateAccountSession = async (
  url: string,
  accessToken: string,
  refreshToken: string,
) => {
  const res = await http.axiosInstance.post(url, {
    access_token: accessToken,
    refresh_token: refreshToken,
  });

  return res.status >= 200 && res.status <= 299;
};

const resendConfirmUser = async (
  url: string,
  emailAddress: string,
  whitelabel: string,
) => {
  try {
    const res = await http.axiosInstance.post(
      url,
      {
        email_address: emailAddress,
      },
      {
        headers: {
          'X-Whitelabel': whitelabel,
        },
      },
    );

    return res.status === 204;
  } catch {
    // Treat any failure to resend the confirmation email as unsuccessful.
    return false;
  }
};

const useAccountRequest = () => {
  const channel = useBroadcastChannel(AUTH_CHANNEL);
  const {
    accessToken,
    refreshToken,
    setAccessToken,
    setRefreshToken,
    setUserAccountId,
    clearAccountState,
  } = useAccount();

  const { whitelabel } = useWhitelabel();
  const { formatApiUrl, formatSessionUrl } = useAccountConfig();

  const mixpanelConsent = useConsent(Services.mixpanelProfiles);
  const brazeConsent = useConsent(Services.braze);

  const isConsentGiven = mixpanelConsent || brazeConsent;

  /**
   * Turns a token pair into a session: the in-memory account state, the
   * server-side session cookie, and the login event.
   *
   * Both ways in end here. The exchange endpoint answers with the same
   * `access_token` and `refresh_token` the login endpoint does, so a social
   * login needs nothing of its own.
   *
   * Answers whether the server created the session. The caller decides what a
   * failure means, so the email form keeps the handling it has.
   */
  const establishSession = useCallback(
    async (newAccessToken: string, newRefreshToken: string) => {
      const urlSession = formatSessionUrl(http.API.accountSession);

      /*
       * The server session comes first. State stored before it would survive a
       * refused session: the header would show the account of a user the
       * caller reports as logged out, and the login event would count an
       * attempt that never became a session.
       */
      const sessionCreated = await updateAccountSession(
        urlSession,
        newAccessToken,
        newRefreshToken,
      );

      if (!sessionCreated) {
        return false;
      }

      setAccessToken(newAccessToken);
      setRefreshToken(newRefreshToken);

      const userAccountId = jwt.decodeToken(newAccessToken)?.username;
      setUserAccountId(userAccountId);

      genericTracking.trackLogin({
        userId: isConsentGiven ? userAccountId : undefined,
      });

      return true;
    },
    [
      formatSessionUrl,
      isConsentGiven,
      setAccessToken,
      setRefreshToken,
      setUserAccountId,
    ],
  );

  const login = useCallback(
    async (username: string, password: string) => {
      const url = formatApiUrl(http.API.accountLogin);

      try {
        const res = await http.axiosInstance.post<
          LoginResponse | LoginErrorResponse
        >(
          url,
          {
            username,
            password,
          },
          {
            headers: {
              'X-Whitelabel': whitelabel,
            },
          },
        );

        if (res.status >= 200 && res.status <= 299) {
          const data = res.data as LoginResponse;

          const sessionEstablished = await establishSession(
            data.access_token,
            data.refresh_token,
          );

          /*
           * Tokens alone are not a session. Without the server-side session the
           * user is signed out again on the next page, so this is a failed
           * login and not a successful one.
           */
          if (!sessionEstablished) {
            return {
              loggedIn: false,
              error: LoginError.Unknown,
            } as LoginData;
          }
        }

        return {
          loggedIn: true,
        } as LoginData;
      } catch (error) {
        if (isAxiosError(error)) {
          return {
            loggedIn: false,
            error: handleLoginError(error.response?.data.type),
          } as LoginData;
        }

        return {
          loggedIn: false,
          error: LoginError.Unknown,
        } as LoginData;
      }
    },
    [establishSession, formatApiUrl, whitelabel],
  );

  const logout = useCallback(async () => {
    const url = formatApiUrl(http.API.accountLogout);
    const accountSessionUrl = formatSessionUrl(http.API.accountSession);

    /*
     * The caller does not await this, so the user expects to see themselves
     * logged out immediately rather than once the network calls below
     * settle. Both calls are still awaited so the return value accurately
     * reports whether the server-side logout actually succeeded, and both
     * are attempted even if one fails: a revocation failure must not skip
     * clearing the session, and vice versa.
     */
    clearAccountState();
    channel.sendMessage({ type: 'logout' });

    let succeeded = true;

    try {
      await http.axiosInstance.post(
        url,
        {
          refresh_token: refreshToken,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-Whitelabel': whitelabel,
          },
        },
      );
    } catch {
      // The refresh token stays valid server-side when this fails.
      succeeded = false;
    }

    try {
      await http.axiosInstance.delete(accountSessionUrl);
    } catch {
      // The server-side session stays alive when this fails.
      succeeded = false;
    }

    return succeeded;
  }, [
    formatApiUrl,
    formatSessionUrl,
    clearAccountState,
    refreshToken,
    accessToken,
    whitelabel,
    channel,
  ]);
  return { establishSession, login, logout };
};

export { resendConfirmUser, LoginError, useAccountRequest };
export type { SessionData };
